// Portal API Client
const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Convert any FastAPI error payload (string, 422 validation array, or object) into a
// clean, human-readable message. Never let a raw object render as "[object Object]".
function prettyField(loc) {
  if (!Array.isArray(loc)) return '';
  const parts = loc.filter((p) => p !== 'body');
  if (!parts.length) return '';
  return parts.join(' → ').replace(/_/g, ' ');
}

export function formatApiError(detail, status) {
  if (detail == null) {
    return status >= 500
      ? 'Unable to complete the request due to a system error. Please try again. If the problem continues, contact an administrator.'
      : 'Request failed';
  }
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const msgs = detail.map((d) => {
      if (typeof d === 'string') return d;
      const field = prettyField(d.loc);
      const msg = d.msg || d.detail || 'invalid value';
      return field ? `${field}: ${msg}` : msg;
    });
    return `Please check the following: ${msgs.join('; ')}`;
  }
  if (typeof detail === 'object') {
    return detail.msg || detail.detail || detail.message || 'Request failed';
  }
  return String(detail);
}

class PortalApi {
  constructor() {
    this.token = localStorage.getItem('calyvra_portal_token');
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('calyvra_portal_token', token);
    } else {
      localStorage.removeItem('calyvra_portal_token');
    }
  }

  getToken() {
    return this.token || localStorage.getItem('calyvra_portal_token');
  }

  async request(endpoint, options = {}, _retry = 0) {
    const url = `${API_BASE}/api/portal${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Only safe (idempotent) reads are auto-retried to avoid duplicate writes
    const isIdempotent = !options.method || options.method === 'GET';
    const MAX_RETRIES = 2;

    let response;
    try {
      response = await fetch(url, {
        ...options,
        headers,
        credentials: 'include',
      });
    } catch (networkErr) {
      // Network failure (e.g. backend cold start / dropped connection)
      if (isIdempotent && _retry < MAX_RETRIES) {
        await new Promise((r) => setTimeout(r, 700 * (_retry + 1)));
        return this.request(endpoint, options, _retry + 1);
      }
      throw new Error('Network error. Please check your connection and try again.');
    }

    // Transient gateway/cold-start errors — retry idempotent reads
    if ([502, 503, 504].includes(response.status) && isIdempotent && _retry < MAX_RETRIES) {
      await new Promise((r) => setTimeout(r, 700 * (_retry + 1)));
      return this.request(endpoint, options, _retry + 1);
    }

    if (response.status === 401 && !options.noAuthRedirect) {
      this.setToken(null);
      window.location.href = '/portal/login';
      throw new Error('Unauthorized');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(formatApiError(error.detail, response.status));
    }

    // Handle empty responses
    const text = await response.text();
    return text ? JSON.parse(text) : null;
  }

  // Auth
  async login(email, password) {
    // Step 1: verify credentials. Returns { mfa_setup_required }, { mfa_required },
    // or { access_token, user } when this device is trusted (MFA within last 24h).
    const data = await this.request('/auth/login', {
      method: 'POST',
      noAuthRedirect: true,
      body: JSON.stringify({ email, password }),
    });
    if (data.access_token) this.setToken(data.access_token);
    return data;
  }

  async mfaSetup(token) {
    return this.request('/auth/mfa/setup', {
      method: 'POST',
      noAuthRedirect: true,
      body: JSON.stringify({ token }),
    });
  }

  async mfaEnable(token, code) {
    const data = await this.request('/auth/mfa/enable', {
      method: 'POST',
      noAuthRedirect: true,
      body: JSON.stringify({ token, code }),
    });
    this.setToken(data.access_token);
    return data;
  }

  async mfaVerify(mfaToken, code) {
    const data = await this.request('/auth/mfa/verify', {
      method: 'POST',
      noAuthRedirect: true,
      body: JSON.stringify({ mfa_token: mfaToken, code }),
    });
    if (data.access_token) this.setToken(data.access_token);
    return data;
  }

  // Passkeys (WebAuthn) — logged-in management
  async listPasskeys() {
    return this.request('/auth/passkeys');
  }

  async passkeyRegisterOptions() {
    return this.request('/auth/passkeys/register/options', { method: 'POST' });
  }

  async passkeyRegisterVerify(credential, label) {
    return this.request('/auth/passkeys/register/verify', {
      method: 'POST',
      body: JSON.stringify({ credential, label }),
    });
  }

  async deletePasskey(id) {
    return this.request(`/auth/passkeys/${id}`, { method: 'DELETE' });
  }

  // Passkeys — optional login step (after password + TOTP)
  async passkeyLoginOptions(passkeyToken) {
    return this.request('/auth/passkeys/login/options', {
      method: 'POST',
      noAuthRedirect: true,
      body: JSON.stringify({ passkey_token: passkeyToken }),
    });
  }

  async passkeyLoginVerify(passkeyToken, credential) {
    const data = await this.request('/auth/passkeys/login/verify', {
      method: 'POST',
      noAuthRedirect: true,
      body: JSON.stringify({ passkey_token: passkeyToken, credential }),
    });
    if (data.access_token) this.setToken(data.access_token);
    return data;
  }

  async resetMfa(userId) {
    return this.request(`/auth/mfa/reset/${userId}`, { method: 'POST' });
  }

  async logout() {
    await this.request('/auth/logout', { method: 'POST' });
    this.setToken(null);
  }

  async getMe() {
    return this.request('/auth/me');
  }

  async changePassword(currentPassword, newPassword) {
    return this.request('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
  }

  // Constants
  async getConstants() {
    return this.request('/constants');
  }

  // Coverage Years
  async getCoverageYears() {
    return this.request('/coverage-years');
  }

  async createCoverageYear(data) {
    return this.request('/coverage-years', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCoverageYear(id, data) {
    return this.request(`/coverage-years/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async createRenewals(sourceId, targetYearId, options = {}) {
    return this.request(`/coverage-years/${sourceId}/renewals`, {
      method: 'POST',
      body: JSON.stringify({
        target_coverage_year_id: targetYearId,
        ...options,
      }),
    });
  }

  async getCaseYearVersions(caseId) {
    return this.request(`/cases/${caseId}/year-versions`);
  }

  async getEnrollmentSummary(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.request(`/cases/enrollment-summary${qs ? `?${qs}` : ''}`);
  }

  async setEnrollmentType(caseId, enrollmentType) {
    return this.request(`/cases/${caseId}/enrollment-type`, {
      method: 'PATCH',
      body: JSON.stringify({ enrollment_type: enrollmentType }),
    });
  }

  async renewCase(caseId, data) {
    // Custom handling so the dialog can read the 409 duplicate detail (existing_case_id) and the
    // 400 missing-fields detail, which the generic request() flattens into a string.
    const res = await fetch(`${API_BASE}/api/portal/cases/${caseId}/renew`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${this.getToken()}` },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const text = await res.text();
    const payload = text ? JSON.parse(text) : null;
    if (res.ok) return payload;
    const err = new Error(formatApiError(payload?.detail, res.status));
    err.status = res.status;
    err.detail = payload?.detail;
    throw err;
  }

  async getPolicyCatalog() {
    return this.request('/policy-catalog');
  }

  async getRenewalTracking(params = {}) {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    ).toString();
    return this.request(`/cases/renewal-tracking${qs ? `?${qs}` : ''}`);
  }

  async cleanupNonActiveRenewals(yearId, workspaceKey) {    return this.request(`/coverage-years/${yearId}/cleanup-renewals`, {
      method: 'POST',
      body: JSON.stringify({ workspace_key: workspaceKey || null }),
    });
  }

  async deleteCase(caseId) {
    return this.request(`/cases/${caseId}`, { method: 'DELETE' });
  }

  async bulkDeleteCases(ids) {
    return this.request('/cases/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
  }

  async bulkCaseStatus(ids, status, reason = null) {
    return this.request('/cases/bulk-status', {
      method: 'POST',
      body: JSON.stringify({ ids, status, reason }),
    });
  }

  // ---- View as user (read-only impersonation) ----
  async impersonate(userId) {
    const data = await this.request(`/impersonate/${userId}`, { method: 'POST' });
    if (data.access_token) {
      const current = this.getToken();
      if (current) localStorage.setItem('calyvra_admin_token', current);
      localStorage.setItem('calyvra_impersonation_name', (data.user && (data.user.name || data.user.email)) || 'user');
      this.setToken(data.access_token);
    }
    return data;
  }

  isImpersonating() {
    return !!localStorage.getItem('calyvra_admin_token');
  }

  impersonationName() {
    return localStorage.getItem('calyvra_impersonation_name') || '';
  }

  stopImpersonation() {
    const admin = localStorage.getItem('calyvra_admin_token');
    if (admin) this.setToken(admin);
    localStorage.removeItem('calyvra_admin_token');
    localStorage.removeItem('calyvra_impersonation_name');
  }

  // Users
  async getUsers(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/users${query ? '?' + query : ''}`);
  }

  async getAssignableUsers() {
    return this.request('/assignable-users');
  }

  async getMentionableUsers(caseId) {
    return this.request(`/cases/${caseId}/mentionable-users`);
  }

  async createUser(data) {
    return this.request('/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUser(id, data) {
    return this.request(`/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteUser(id) {
    return this.request(`/users/${id}`, { method: 'DELETE' });
  }

  async setUserActive(id, disabled) {
    return this.request(`/users/${id}/active?disabled=${disabled}`, { method: 'PATCH' });
  }

  async resetUserPassword(id) {
    return this.request(`/users/${id}/reset-password`, { method: 'POST' });
  }

  async getUserReferences(id) {
    return this.request(`/users/${id}/references`);
  }

  async transferCases(fromId, toId) {
    return this.request(`/users/${fromId}/transfer-cases?to_case_manager_id=${toId}`, {
      method: 'POST',
    });
  }

  // Cases
  async getCases(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/cases${query ? '?' + query : ''}`);
  }

  async getCase(id, revealSsn = false) {
    return this.request(`/cases/${id}?reveal_ssn=${revealSsn}`);
  }

  async createCase(data) {
    return this.request('/cases', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCase(id, data) {
    return this.request(`/cases/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async changeCaseStatus(id, status, reason = null) {
    return this.request(`/cases/${id}/status?status=${status}${reason ? '&reason=' + encodeURIComponent(reason) : ''}`, {
      method: 'POST',
    });
  }

  async updateCasePolicy(id, policy) {
    return this.request(`/cases/${id}/policy`, {
      method: 'PUT',
      body: JSON.stringify(policy),
    });
  }

  // Policies (history)
  async getPolicies(caseId) {
    return this.request(`/cases/${caseId}/policies`);
  }

  async createPolicy(caseId, data) {
    return this.request(`/cases/${caseId}/policies`, { method: 'POST', body: JSON.stringify(data) });
  }

  async updatePolicy(caseId, policyId, data) {
    return this.request(`/cases/${caseId}/policies/${policyId}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deletePolicy(caseId, policyId) {
    return this.request(`/cases/${caseId}/policies/${policyId}`, { method: 'DELETE' });
  }

  async getPolicyHistory(athenaId) {
    return this.request(`/policy-history/${athenaId}`);
  }

  // Intake Types (settings)
  async getIntakeTypes(coverageYearId) {
    const q = coverageYearId ? `?coverage_year_id=${coverageYearId}` : '';
    return this.request(`/intake-types${q}`);
  }

  async getActiveIntakeTypes(coverageYearId) {
    const q = coverageYearId ? `?coverage_year_id=${coverageYearId}` : '';
    return this.request(`/intake-types/active${q}`);
  }

  async createIntakeType(name) {
    return this.request('/intake-types', { method: 'POST', body: JSON.stringify({ name }) });
  }

  async updateIntakeType(id, data) {
    return this.request(`/intake-types/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  // Workspaces
  async getWorkspaces() {
    return this.request('/workspaces');
  }

  async getAllWorkspaces() {
    return this.request('/workspaces/all');
  }

  async createWorkspace(data) {
    return this.request('/workspaces', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateWorkspace(id, data) {
    return this.request(`/workspaces/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async getWorkspaceUsers(id) {
    return this.request(`/workspaces/${id}/users`);
  }

  async setWorkspaceUserAccess(id, userId, grant) {
    return this.request(`/workspaces/${id}/users/${userId}?grant=${grant}`, { method: 'POST' });
  }

  // Clients
  async getClients(params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') qs.append(k, v); });
    return this.request(`/clients?${qs.toString()}`);
  }

  async getGeography(params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '' && v !== 'all') qs.append(k, v); });
    return this.request(`/analytics/geography?${qs.toString()}`);
  }

  async getGeographyEntities(params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '' && v !== 'all') qs.append(k, v); });
    return this.request(`/analytics/geography/entities?${qs.toString()}`);
  }

  async getClient(id) {
    return this.request(`/clients/${id}`);
  }

  async createClient(data) {
    return this.request('/clients', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateClient(id, data) {
    return this.request(`/clients/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async transferClient(id, data) {
    return this.request(`/clients/${id}/transfer`, { method: 'POST', body: JSON.stringify(data) });
  }

  // Client access requests (agent → admin)
  async requestClientAccess(clientId, reason) {
    return this.request(`/clients/${clientId}/request-access`, {
      method: 'POST', body: JSON.stringify({ reason: reason || null }),
    });
  }

  async getAccessRequests(status = 'pending') {
    return this.request(`/access-requests?status=${status}`);
  }

  async approveAccessRequest(id) {
    return this.request(`/access-requests/${id}/approve`, { method: 'POST' });
  }

  async denyAccessRequest(id) {
    return this.request(`/access-requests/${id}/deny`, { method: 'POST' });
  }

  // Agent assignment extras
  async setAgentAction(clientId, message, required) {
    return this.request(`/clients/${clientId}/agent-action`, {
      method: 'POST', body: JSON.stringify({ message, required: !!required }),
    });
  }

  async resolveAgentAction(clientId) {
    return this.request(`/clients/${clientId}/agent-action/resolve`, { method: 'POST' });
  }

  async bulkAssignClients(payload) {
    return this.request('/clients/bulk-assign', { method: 'POST', body: JSON.stringify(payload) });
  }

  async getAgentSummary() {
    return this.request('/agent/summary');
  }

  async getUserClientLoad(userId) {
    return this.request(`/users/${userId}/client-load`);
  }

  async reassignAgentClients(userId, toAgentId) {
    const q = toAgentId ? `?to_agent_id=${encodeURIComponent(toAgentId)}` : '';
    return this.request(`/users/${userId}/reassign-clients${q}`, { method: 'POST' });
  }

  async getTeamOverview() {
    return this.request('/team/overview');
  }

  async assignDefaultAgent(coverageYearId, agentId) {
    return this.request('/renewals/assign-default-agent', {
      method: 'POST',
      body: JSON.stringify({ coverage_year_id: coverageYearId, agent_id: agentId }),
    });
  }

  async backfillEnrollmentType(coverageYearId, newAgentId, workspaceKey = 'latinos_salud') {
    return this.request('/renewals/backfill-enrollment-type', {
      method: 'POST',
      body: JSON.stringify({ coverage_year_id: coverageYearId, new_agent_id: newAgentId, workspace_key: workspaceKey }),
    });
  }

  async normalizeProgramType(fromValue, toValue, workspaceKey) {
    return this.request('/cases/normalize-program-type', {
      method: 'POST',
      body: JSON.stringify({ from_value: fromValue, to_value: toValue, workspace_key: workspaceKey || null }),
    });
  }

  async getTrash() { return this.request('/trash'); }
  async restoreTrash(trashId) { return this.request(`/trash/${trashId}/restore`, { method: 'POST' }); }
  async purgeTrash(trashId) { return this.request(`/trash/${trashId}`, { method: 'DELETE' }); }

  // ----- Reports module -----
  async getReportFilterOptions(workspaceId) {
    const q = workspaceId ? `?workspace_id=${encodeURIComponent(workspaceId)}` : '';
    return this.request(`/reports/filter-options${q}`);
  }
  async reportPremiumAssistance(filters) {
    return this.request('/reports/premium-assistance', { method: 'POST', body: JSON.stringify(filters) });
  }
  async reportEnrollmentRenewals(filters) {
    return this.request('/reports/enrollment-renewals', { method: 'POST', body: JSON.stringify(filters) });
  }
  async reportBuilder(body) {
    return this.request('/reports/builder', { method: 'POST', body: JSON.stringify(body) });
  }
  async reportDrillDown(body) {
    return this.request('/reports/drill-down', { method: 'POST', body: JSON.stringify(body) });
  }
  async getFplGuidelines() {
    return this.request('/reports/fpl-guidelines');
  }
  async updateFplGuideline(year, body) {
    return this.request(`/reports/fpl-guidelines/${year}`, { method: 'PUT', body: JSON.stringify(body) });
  }
  async exportReport(body) {
    const url = `${API_BASE}/api/portal/reports/export`;
    const resp = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${this.getToken()}` },
      credentials: 'include',
      body: JSON.stringify(body),
    });
    if (!resp.ok) throw new Error('Export failed');
    const blob = await resp.blob();
    const a = document.createElement('a');
    a.href = window.URL.createObjectURL(blob);
    a.download = `${body.report}_${body.type}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
  }

  async getCaseDemographics(caseId) {
    return this.request(`/cases/${caseId}/demographics`);
  }

  async checkClientDuplicates(data) {
    return this.request('/clients/check-duplicates', { method: 'POST', body: JSON.stringify(data) });
  }

  async revealClientSsn(id) {
    return this.request(`/clients/${id}/ssn`);
  }

  async createClientCase(id, data) {
    return this.request(`/clients/${id}/cases`, { method: 'POST', body: JSON.stringify(data) });
  }

  async addClientWorkspace(id, data) {
    return this.request(`/clients/${id}/workspaces`, { method: 'POST', body: JSON.stringify(data) });
  }

  // Client-centric (Standard Calyvra) — policies, documents, notes on the client
  async getClientPolicies(id) { return this.request(`/clients/${id}/policies`); }
  async createClientPolicy(id, data) { return this.request(`/clients/${id}/policies`, { method: 'POST', body: JSON.stringify(data) }); }
  async updateClientPolicy(id, policyId, data) { return this.request(`/clients/${id}/policies/${policyId}`, { method: 'PATCH', body: JSON.stringify(data) }); }
  async deleteClientPolicy(id, policyId) { return this.request(`/clients/${id}/policies/${policyId}`, { method: 'DELETE' }); }

  async getClientDocuments(id) { return this.request(`/clients/${id}/documents`); }
  async createClientDocument(id, data) { return this.request(`/clients/${id}/documents`, { method: 'POST', body: JSON.stringify(data) }); }
  async updateClientDocument(id, docId, data) { return this.request(`/clients/${id}/documents/${docId}`, { method: 'PATCH', body: JSON.stringify(data) }); }
  async deleteClientDocument(id, docId) { return this.request(`/clients/${id}/documents/${docId}`, { method: 'DELETE' }); }

  async getClientNotes(id) { return this.request(`/clients/${id}/notes`); }
  async addClientNote(id, payload) {
    const body = typeof payload === 'string' ? { text: payload } : payload;
    return this.request(`/clients/${id}/notes`, { method: 'POST', body: JSON.stringify(body) });
  }

  async getAgents() {
    return this.request('/agents');
  }

  async getProducer(id) { return this.request(`/producers/${id}`); }
  async updateProducer(id, data) { return this.request(`/producers/${id}`, { method: 'PATCH', body: JSON.stringify(data) }); }
  async addProducerLicense(id, data) { return this.request(`/producers/${id}/licenses`, { method: 'POST', body: JSON.stringify(data) }); }
  async updateProducerLicense(id, lid, data) { return this.request(`/producers/${id}/licenses/${lid}`, { method: 'PATCH', body: JSON.stringify(data) }); }
  async deleteProducerLicense(id, lid) { return this.request(`/producers/${id}/licenses/${lid}`, { method: 'DELETE' }); }
  async addProducerCarrier(id, data) { return this.request(`/producers/${id}/carriers`, { method: 'POST', body: JSON.stringify(data) }); }
  async updateProducerCarrier(id, cid, data) { return this.request(`/producers/${id}/carriers/${cid}`, { method: 'PATCH', body: JSON.stringify(data) }); }
  async deleteProducerCarrier(id, cid) { return this.request(`/producers/${id}/carriers/${cid}`, { method: 'DELETE' }); }
  async uploadLicenseFile(id, lid, file) {
    const formData = new FormData();
    formData.append('file', file);
    const token = this.getToken();
    const response = await fetch(`${API_BASE}/api/portal/producers/${id}/licenses/${lid}/upload`, {
      method: 'POST', headers: token ? { 'Authorization': `Bearer ${token}` } : {}, credentials: 'include', body: formData,
    });
    if (!response.ok) { const e = await response.json().catch(() => ({ detail: 'Upload failed' })); throw new Error(e.detail || 'Upload failed'); }
    return response.json();
  }
  getLicenseFileUrl(id, lid, fileId) {
    // Auth via httponly session cookie (sent automatically); no token in URL.
    return `${API_BASE}/api/portal/producers/${id}/licenses/${lid}/files/${fileId}`;
  }
  async deleteLicenseFile(id, lid, fileId) { return this.request(`/producers/${id}/licenses/${lid}/files/${fileId}`, { method: 'DELETE' }); }

  async notifyManager(caseId, reason) {
    return this.request(`/cases/${caseId}/notify-manager?reason=${encodeURIComponent(reason)}`, {
      method: 'POST',
    });
  }

  async resolveAttention(caseId) {
    return this.request(`/cases/${caseId}/resolve-attention`, { method: 'POST' });
  }

  async deleteDocument(caseId, docId) {
    return this.request(`/cases/${caseId}/documents/${docId}`, { method: 'DELETE' });
  }

  async deleteDocumentFile(caseId, docId, fileId) {
    return this.request(`/cases/${caseId}/documents/${docId}/files/${fileId}`, { method: 'DELETE' });
  }

  async bulkUpdateCases(caseIds, setData) {
    return this.request('/cases/bulk-update', {
      method: 'POST',
      body: JSON.stringify({ case_ids: caseIds, set: setData }),
    });
  }

  // Documents
  async getDocuments(caseId) {
    return this.request(`/cases/${caseId}/documents`);
  }

  async createDocument(caseId, data) {
    return this.request(`/cases/${caseId}/documents`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateDocumentStatus(caseId, docId, status) {
    return this.request(`/cases/${caseId}/documents/${docId}/status?status=${status}`, {
      method: 'PATCH',
    });
  }

  async uploadDocumentFile(caseId, docId, file) {
    const formData = new FormData();
    formData.append('file', file);

    const token = this.getToken();
    const response = await fetch(`${API_BASE}/api/portal/cases/${caseId}/documents/${docId}/upload`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(error.detail || 'Upload failed');
    }

    return response.json();
  }

  getDocumentFileUrl(caseId, docId, fileId) {
    // Auth via httponly session cookie (sent automatically); no token in URL.
    return `${API_BASE}/api/portal/cases/${caseId}/documents/${docId}/files/${fileId}`;
  }

  // Notes / Chat
  async getNotes(caseId) {
    return this.request(`/cases/${caseId}/notes`);
  }

  async addNote(caseId, payload) {
    // Backward compatible: accept a string or a rich payload
    const body = typeof payload === 'string'
      ? { text: payload }
      : payload;
    return this.request(`/cases/${caseId}/notes`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async uploadCaseAttachment(caseId, file) {
    const formData = new FormData();
    formData.append('file', file);
    const token = this.getToken();
    const response = await fetch(`${API_BASE}/api/portal/cases/${caseId}/attachments`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      credentials: 'include',
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(error.detail || 'Upload failed');
    }
    return response.json();
  }

  getAttachmentUrl(caseId, storagePath, filename) {
    // Auth via httponly session cookie (sent automatically); no token in URL.
    const q = new URLSearchParams({ path: storagePath, name: filename || 'file' });
    return `${API_BASE}/api/portal/cases/${caseId}/attachments/download?${q.toString()}`;
  }

  // Alerts
  async getAlerts(caseId) {
    return this.request(`/cases/${caseId}/alerts`);
  }

  async createAlert(caseId, data) {
    return this.request(`/cases/${caseId}/alerts`, { method: 'POST', body: JSON.stringify(data) });
  }

  async updateAlert(caseId, alertId, data) {
    return this.request(`/cases/${caseId}/alerts/${alertId}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async addAlertComment(caseId, alertId, data) {
    return this.request(`/cases/${caseId}/alerts/${alertId}/comments`, { method: 'POST', body: JSON.stringify(data) });
  }

  // Notifications
  async getNotifications(unreadOnly = false) {
    return this.request(`/notifications${unreadOnly ? '?unread_only=true' : ''}`);
  }

  async markNotificationRead(id) {
    return this.request(`/notifications/${id}/read`, { method: 'POST' });
  }

  async markAllNotificationsRead() {
    return this.request('/notifications/read-all', { method: 'POST' });
  }

  // Audit
  async getCaseAudit(caseId) {
    return this.request(`/cases/${caseId}/audit`);
  }

  // Dashboard
  async getDashboardStats(coverageYearId = null, workspaceId = null) {
    const qs = new URLSearchParams();
    if (coverageYearId) qs.append('coverage_year_id', coverageYearId);
    if (workspaceId) qs.append('workspace_id', workspaceId);
    const query = qs.toString() ? `?${qs.toString()}` : '';
    return this.request(`/dashboard/stats${query}`);
  }

  async getCarriers() {
    return this.request('/dashboard/carriers');
  }

  async getBookOfBusiness(params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') qs.append(k, v); });
    return this.request(`/dashboard/book-of-business?${qs.toString()}`);
  }

  async getMissingDocuments(workspaceId) {
    return this.request(`/dashboard/missing-documents?workspace_id=${workspaceId}`);
  }

  async bulkClientDemographics(csvText, dryRun, filename = null) {
    return this.request('/clients/bulk-demographics', { method: 'POST', body: JSON.stringify({ csv_text: csvText, dry_run: dryRun, filename }) });
  }

  getEnrichTemplateUrl() {
    return `${API_BASE}/api/portal/clients/enrich/template`;
  }

  async backfillLsCases(dryRun = true) {
    return this.request(`/admin/backfill-ls-cases?dry_run=${dryRun}`, { method: 'POST' });
  }

  getExportUrl(coverageYearId = null) {
    const query = coverageYearId ? `?coverage_year_id=${coverageYearId}` : '';
    return `${API_BASE}/api/portal/dashboard/export${query}`;
  }

  async getCaseExportFields(workspaceId) {
    const qs = workspaceId ? `?workspace_id=${workspaceId}` : '';
    return this.request(`/case-export-fields${qs}`);
  }

  async exportCasesCustom(payload) {
    const token = this.getToken();
    const response = await fetch(`${API_BASE}/api/portal/case-export`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    if (!response.ok) { const e = await response.json().catch(() => ({ detail: 'Export failed' })); throw new Error(e.detail || 'Export failed'); }
    return response.blob();
  }

  // Import
  getImportTemplateUrl() {
    return `${API_BASE}/api/portal/cases/import/template`;
  }

  async previewImport(file) {
    const formData = new FormData();
    formData.append('file', file);

    const token = this.getToken();
    const response = await fetch(`${API_BASE}/api/portal/cases/import/preview`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Preview failed' }));
      throw new Error(error.detail || 'Preview failed');
    }

    return response.json();
  }

  async commitImport(coverageYearId, onDuplicate, rows) {
    return this.request(`/cases/import/commit?coverage_year_id=${coverageYearId}&on_duplicate=${onDuplicate}`, {
      method: 'POST',
      body: JSON.stringify(rows),
    });
  }

  // Audit log panel (Calyvra Administrators only)
  async getAuditLogs(params = {}) {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    ).toString();
    return this.request(`/audit-logs${qs ? `?${qs}` : ''}`);
  }

  async exportAuditLogs(params = {}) {
    const token = this.getToken();
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    ).toString();
    const response = await fetch(`${API_BASE}/api/portal/audit-logs/export${qs ? `?${qs}` : ''}`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      credentials: 'include',
    });
    if (!response.ok) { const e = await response.json().catch(() => ({ detail: 'Export failed' })); throw new Error(e.detail || 'Export failed'); }
    return response.blob();
  }

  // ===== Oscar Premium Payment Management (Calyvra Admin only) =====
  async oscarDashboard() { return this.request('/oscar/dashboard'); }
  async oscarMetrics() { return this.request('/oscar/metrics'); }
  async oscarSettings() { return this.request('/oscar/settings'); }
  async oscarUpdateSettings(body) { return this.request('/oscar/settings', { method: 'PUT', body: JSON.stringify(body) }); }
  async oscarBatches() { return this.request('/oscar/batches'); }
  async oscarImport(body) { return this.request('/oscar/members/import', { method: 'POST', body: JSON.stringify(body) }); }
  async oscarMembers(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') q.append(k, v); });
    return this.request(`/oscar/members?${q.toString()}`);
  }
  async oscarMemberDetail(id) { return this.request(`/oscar/members/${id}/detail`); }
  async oscarRetryMember(id) { return this.request(`/oscar/members/${id}/retry`, { method: 'POST' }); }
  async oscarCallHistory(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') q.append(k, v); });
    return this.request(`/oscar/calls?${q.toString()}`);
  }
  async oscarPurgeRecordings() { return this.request('/oscar/recordings/purge', { method: 'POST' }); }
  async oscarVerifyProgress() { return this.request('/oscar/verify/progress'); }
  async oscarVerifyStart() { return this.request('/oscar/verify/start', { method: 'POST' }); }

  // ===== Phase 2 — ACH Payments (Stage 1) =====
  async oscarAchGet() { return this.request('/oscar/pay/ach/account'); }
  async oscarAchSet(body) { return this.request('/oscar/pay/ach/account', { method: 'PUT', body: JSON.stringify(body) }); }
  async oscarPayable() { return this.request('/oscar/pay/payable'); }
  async oscarReconcile() { return this.request('/oscar/pay/reconcile', { method: 'POST' }); }
  async oscarResetData(body) { return this.request('/oscar/data/reset', { method: 'POST', body: JSON.stringify(body) }); }
  async oscarApprovePayment(ref, body) { return this.request(`/oscar/pay/${ref}/approve`, { method: 'POST', body: JSON.stringify(body) }); }
  async oscarPaymentDetail(ref) { return this.request(`/oscar/pay/${ref}`); }
  async oscarRejectPayment(ref) { return this.request(`/oscar/pay/${ref}/reject`, { method: 'POST' }); }
  async oscarPaySimulate(body) { return this.request('/oscar/pay/simulate', { method: 'POST', body: JSON.stringify(body) }); }
  async oscarCompare() { return this.request('/oscar/pay/compare'); }
  async oscarGroup(group) { return this.request(`/oscar/pay/groups/${group}`); }
  async oscarMatchedSummary() { return this.request('/oscar/pay/matched/summary'); }
  async oscarApproveMatchedBatch(body) { return this.request('/oscar/pay/matched/approve-batch', { method: 'POST', body: JSON.stringify(body) }); }
  async oscarPayResults() { return this.request('/oscar/pay/results'); }
  async oscarProcessBatch() { return this.request('/oscar/pay/matched/process-batch', { method: 'POST' }); }
  async oscarPilotPay(body) { return this.request('/oscar/pay/pilot/pay-member', { method: 'POST', body: JSON.stringify(body) }); }
  async oscarDirectPay(body) { return this.request('/oscar/pay/direct/pay-member', { method: 'POST', body: JSON.stringify(body) }); }
  async oscarVerifyCallers() { return this.request('/oscar/pay/caller-numbers/verify'); }
  async oscarResolveMismatch(ref, body) { return this.request(`/oscar/pay/${ref}/resolve-mismatch`, { method: 'POST', body: JSON.stringify(body) }); }
  async oscarExecutionProgress() { return this.request('/oscar/pay/execution/progress'); }
  async oscarDownload(path, filename) {
    const response = await fetch(`${API_BASE}/api/portal/oscar${path}`, {
      headers: { Authorization: `Bearer ${this.getToken()}` }, credentials: 'include',
    });
    if (!response.ok) throw new Error('Export failed');
    const blob = await response.blob();
    const a = document.createElement('a');
    a.href = window.URL.createObjectURL(blob); a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
  }
  async oscarStartBatch(id) { return this.request(`/oscar/batches/${id}/start`, { method: 'POST' }); }
  async oscarPauseBatch(id) { return this.request(`/oscar/batches/${id}/pause`, { method: 'POST' }); }
  async oscarStopBatch(id) { return this.request(`/oscar/batches/${id}/stop`, { method: 'POST' }); }
  async oscarRetryFailed(id) { return this.request(`/oscar/batches/${id}/retry-failed`, { method: 'POST' }); }
  async oscarSimulate(body) { return this.request('/oscar/simulate', { method: 'POST', body: JSON.stringify(body) }); }
  async oscarExport(batchId) {
    const q = batchId ? `?batch_id=${batchId}` : '';
    const response = await fetch(`${API_BASE}/api/portal/oscar/members/export${q}`, {
      headers: { Authorization: `Bearer ${this.getToken()}` }, credentials: 'include',
    });
    if (!response.ok) throw new Error('Export failed');
    const blob = await response.blob();
    const a = document.createElement('a');
    a.href = window.URL.createObjectURL(blob); a.download = 'oscar_members.csv';
    document.body.appendChild(a); a.click(); a.remove();
  }
}

export const portalApi = new PortalApi();
export default portalApi;
